import Iconify from '@emfood/shared/src/components/iconify';

import { Box, Badge, Stack, Tooltip, Typography, ButtonBase } from '@mui/material';

import CustomPopover, { usePopover } from 'src/components/custom-popover';

// Sustainability / certification "key feature" indicator shown next to a product
// name in the marketplace search results (AC-2031).
//
// Space in the product-name table column is tight, so instead of laying every
// badge out inline we show ONE icon (the first in order) with a "+N" count badge
// for the rest. The whole control is clickable and opens a popover listing every
// credential with its name.
const ORGANIC_ICON = '/assets/icons/certifications/organic.png';
const RED_TRACTOR_ICON = '/assets/icons/certifications/red-tractor.png';

// Badge slots are ~30% taller than they are wide: the certification PNGs are tall
// (1463x2095) portrait "cards", so a square slot crops them badly (Red Tractor in
// particular). The extra height lets the portrait logo show without heavy cropping.
const TRIGGER_WIDTH = 32;
const TRIGGER_HEIGHT = 42;
const POPOVER_ICON_WIDTH = 36;
const POPOVER_ICON_HEIGHT = 47;
// The logos sit in lots of white padding, so at badge size they look tiny. We crop
// to the slot (objectFit 'cover' + overflow hidden) and zoom into the centred logo
// so it fills the slot like the leaf icon. Long-term fix: ship tightly-cropped assets.
const CERT_LOGO_ZOOM = 1.6;

// A single quality badge: either a certification logo (`src`) or an iconify icon
// (the local-producer leaf — a derived flag, not a certification).
interface QualityBadge {
  label: string;
  src?: string;
  icon?: string;
}

// The local-producer badge is shared with the product-details page so both surfaces show
// the same icon and wording. The 50-mile radius mirrors the backend default
// (LOCAL_PRODUCER_DISTANCE_MILES in ac-marketplace).
export const LOCAL_PRODUCER_BADGE_ICON = 'mdi:map-marker-radius-outline';
export const LOCAL_PRODUCER_BADGE_LABEL = 'Local Product';
export const LOCAL_PRODUCER_BADGE_DESCRIPTION = 'Producer within 50 miles of your site';

interface ProductQualityBadgesProps {
  certifications: string[];
  dietaryLifestyles: string[];
  isLocalProducer: boolean;
}

function BadgeVisual({
  badge,
  width,
  height,
}: {
  badge: QualityBadge;
  width: number;
  height: number;
}) {
  if (badge.src) {
    return (
      <Box
        sx={{
          width,
          height,
          flexShrink: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={badge.src}
          alt={badge.label}
          loading="lazy"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: `scale(${CERT_LOGO_ZOOM})`,
          }}
        />
      </Box>
    );
  }
  return (
    <Iconify
      icon={badge.icon ?? LOCAL_PRODUCER_BADGE_ICON}
      width={Math.min(width, height)}
      sx={{ color: 'success.main' }}
    />
  );
}

export default function ProductQualityBadges({
  certifications,
  dietaryLifestyles,
  isLocalProducer,
}: ProductQualityBadgesProps) {
  const popover = usePopover();

  // The three possible badges, in display order. The first present one is shown
  // inline; a "+N" badge covers the rest. (Organic can be tagged as a certification
  // or a dietary lifestyle, so check both.)
  const badges = [
    (certifications?.includes('Organic') || dietaryLifestyles?.includes('Organic')) && {
      label: 'Organic',
      src: ORGANIC_ICON,
    },
    certifications?.includes('Red Tractor') && {
      label: 'Red Tractor certified',
      src: RED_TRACTOR_ICON,
    },
    isLocalProducer && {
      label: `${LOCAL_PRODUCER_BADGE_LABEL} — ${LOCAL_PRODUCER_BADGE_DESCRIPTION.toLowerCase()}`,
      icon: LOCAL_PRODUCER_BADGE_ICON,
    },
  ].filter(Boolean) as QualityBadge[];

  if (badges.length === 0) {
    return null;
  }

  const [primary] = badges;
  const extraCount = badges.length - 1;
  const tooltipTitle = extraCount > 0 ? 'View sustainability credentials' : primary.label;

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Tooltip title={tooltipTitle}>
        <Badge
          color="success"
          badgeContent={`+${extraCount}`}
          invisible={extraCount === 0}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.625rem',
              fontWeight: 700,
              height: 16,
              minWidth: 16,
              p: '0 4px',
            },
          }}
        >
          <ButtonBase
            onClick={(event) => {
              event.stopPropagation();
              popover.onOpen(event);
            }}
            aria-label={extraCount > 0 ? `${primary.label} and ${extraCount} more` : primary.label}
            sx={{
              width: TRIGGER_WIDTH,
              height: TRIGGER_HEIGHT,
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <BadgeVisual badge={primary} width={TRIGGER_WIDTH} height={TRIGGER_HEIGHT} />
          </ButtonBase>
        </Badge>
      </Tooltip>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="top-left"
        sx={{ p: 1, maxWidth: 300 }}
      >
        <Stack spacing={0.25}>
          {badges.map((badge) => (
            <Stack
              key={badge.label}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ px: 1, py: 0.75 }}
            >
              <BadgeVisual badge={badge} width={POPOVER_ICON_WIDTH} height={POPOVER_ICON_HEIGHT} />
              <Typography variant="body2">{badge.label}</Typography>
            </Stack>
          ))}
        </Stack>
      </CustomPopover>
    </Box>
  );
}
